import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { MODAL_DATA, MODAL_REF, ModalRef } from '@services/components/ModalService/modal.ref';
import { ClpCurrencyPipe } from '@shared/pipes/clp-currency.pipe';

export interface CartRemovedLine {
  name: string;
  reason: string;
}

export interface CartRepricedLine {
  name: string;
  previousPrice: number;
  unitPrice: number;
}

export interface CartChangesModalData {
  removed: CartRemovedLine[];
  repriced: CartRepricedLine[];
  isCartEmpty: boolean;
}

@Component({
  selector: 'app-cart-changes-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ClpCurrencyPipe],
  template: `
    <div class="cart-changes-modal">
      @if (removed.length) {
        <p class="cart-changes-modal__intro">
          {{ removed.length === 1 ? 'Sacamos este producto de tu carrito:' : 'Sacamos estos productos de tu carrito:' }}
        </p>
        <ul class="cart-changes-modal__list">
          @for (line of removed; track line.name) {
            <li class="cart-changes-modal__line">
              <span class="cart-changes-modal__name">{{ line.name }}</span>
              <span class="cart-changes-modal__reason">{{ line.reason }}</span>
            </li>
          }
        </ul>
      }

      @if (repriced.length) {
        <p class="cart-changes-modal__intro">
          {{ repriced.length === 1 ? 'Actualizamos este precio:' : 'Actualizamos estos precios:' }}
        </p>
        <ul class="cart-changes-modal__list">
          @for (line of repriced; track line.name) {
            <li class="cart-changes-modal__line">
              <span class="cart-changes-modal__name">{{ line.name }}</span>
              <span class="cart-changes-modal__reason">
                <s>{{ line.previousPrice | clpCurrency }}</s> → {{ line.unitPrice | clpCurrency }}
              </span>
            </li>
          }
        </ul>
      }

      @if (isCartEmpty) {
        <p class="cart-changes-modal__note">
          Tu carrito quedó vacío. Te llevamos de vuelta al catálogo.
        </p>
      }

      <div class="cart-changes-modal__actions">
        @if (!isCartEmpty) {
          <button class="btn btn-secondary" type="button" (click)="onDismiss()">
            Seguir comprando
          </button>
        }
        <button class="btn btn-primary" type="button" (click)="onGoToCatalog()">
          Ver catálogo
        </button>
      </div>
    </div>
  `,
  styles: [`
    .cart-changes-modal__intro {
      font-family: 'Space Mono', monospace;
      font-size: 0.875rem;
      color: var(--text-dark);
      margin: 0 0 0.75rem;
      line-height: 1.6;
    }

    .cart-changes-modal__list {
      list-style: none;
      margin: 0 0 1.25rem;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .cart-changes-modal__line {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      border-left: 3px solid var(--accent-red);
      padding-left: 0.75rem;
    }

    .cart-changes-modal__name {
      font-family: 'Space Mono', monospace;
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--text-dark);
    }

    .cart-changes-modal__reason {
      font-family: 'Space Mono', monospace;
      font-size: 0.8125rem;
      color: var(--text-dark);
      opacity: 0.75;
    }

    .cart-changes-modal__note {
      font-family: 'Space Mono', monospace;
      font-size: 0.875rem;
      color: var(--text-dark);
      margin: 0 0 1.5rem;
      line-height: 1.6;
    }

    .cart-changes-modal__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    .btn {
      padding: 0.6rem 1.4rem;
      font-family: 'Space Mono', monospace;
      font-size: 0.875rem;
      font-weight: 700;
      border: 2px solid var(--text-dark);
      cursor: pointer;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }

    .btn:hover {
      transform: translate(-2px, -2px);
      box-shadow: 4px 4px 0 var(--text-dark);
    }

    .btn-primary {
      background-color: var(--accent-red);
      color: #ffffff;
    }

    .btn-secondary {
      background-color: transparent;
      color: var(--text-dark);
    }
  `],
})
export class CartChangesModalComponent {
  private readonly modalRef = inject(MODAL_REF) as ModalRef<boolean>;
  private readonly data = inject<CartChangesModalData>(MODAL_DATA);

  public readonly removed = this.data?.removed ?? [];
  public readonly repriced = this.data?.repriced ?? [];
  public readonly isCartEmpty = this.data?.isCartEmpty ?? false;

  /** `confirm` = el cliente quiere ir al catálogo. */
  public onGoToCatalog(): void {
    this.modalRef.close({ reason: 'confirm', data: true });
  }

  public onDismiss(): void {
    this.modalRef.close({ reason: 'cancel', data: false });
  }
}
